import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DocumentTextIcon, ArrowLeftIcon, PencilIcon, PlayIcon } from '@heroicons/react/outline';
import { Dialog } from '@headlessui/react';
import Modal from '../../../../common/Modal';
import { toastService } from '../../../../../services/toast';
import { examsService } from '../../../../../services/exams';
import Content from '../../../../../pages/course-learn/Content';
import { BASE_URL } from '../../../../../config/axios';

const ExamPreviewPage = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [showExam, setShowExam] = useState(false);
  
  // حالات إدارة الاختبار
  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showDetailedResults, setShowDetailedResults] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(60 * 20); // 20 دقيقة افتراضياً
  
  // دالة لمعالجة اختيار الإجابة
  const handleAnswerSelect = useCallback((answer) => {
    setCurrentAnswer(answer);
    setQuizAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: answer
    }));
  }, [currentQuestionIndex]);
  
  // دوال التنقل بين الأسئلة
  const handleNextQuestion = useCallback(() => {
    if (currentQuestionIndex < (exam?.questions?.length || 0) - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setCurrentAnswer(quizAnswers[currentQuestionIndex + 1] || null);
    }
  }, [currentQuestionIndex, exam?.questions, quizAnswers]);
  
  const handlePreviousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setCurrentAnswer(quizAnswers[currentQuestionIndex - 1] || null);
    }
  }, [currentQuestionIndex, quizAnswers]);
  
  // إعادة تعيين الاختبار
  const handleQuizReset = useCallback(() => {
    setIsQuizStarted(false);
    setShowResults(false);
    setShowDetailedResults(false);
    setCurrentQuestionIndex(0);
    setCurrentAnswer(null);
    setQuizAnswers({});
    setTimeLeft(60 * 20);
  }, []);

  useEffect(() => {
    const fetchExam = async () => {
      try {
        setLoading(true);
        const data = await examsService.getExam(examId);
        // ترتيب الأسئلة حسب الحقل order
        if (data.questions && data.questions.length > 0) {
          data.questions.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
        setExam(data);
      } catch (error) {
        console.error('Error fetching exam:', error);
        toastService.error('حدث خطأ أثناء تحميل بيانات الاختبار');
      } finally {
        setLoading(false);
      }
    };

    fetchExam();
  }, [examId]);

  useEffect(() => {
    if (exam) {
      // Exam data loaded
    }
  }, [exam]);

  const handleSaveQuestion = async () => {
    try {
      const response = await examsService.updateQuestion(examId, editingQuestion.id, editingQuestion);
      
      // تحديث الحالة محليًا
      setExam(prevExam => ({
        ...prevExam,
        questions: prevExam.questions.map(q => 
          q.id === editingQuestion.id ? editingQuestion : q
        )
      }));
      setEditingQuestion(null);
      toastService.success('تم تحديث السؤال بنجاح');
    } catch (error) {
      console.error('Error saving question:', error);
      toastService.error('حدث خطأ أثناء حفظ السؤال');
    }
  };

  const handleSaveAnswer = async () => {
    try {
      const question = exam.questions.find(q => q.answers.some(a => a.id === editingAnswer.id));
      if (!question) throw new Error('Question not found');

      const updatedQuestion = { ...question };
        
      // If this answer is being marked as correct, unmark all other answers
      if (editingAnswer.is_correct) {
        updatedQuestion.answers = updatedQuestion.answers.map(a => 
          a.id === editingAnswer.id ? editingAnswer : { ...a, is_correct: false }
        );
      } else {
        updatedQuestion.answers = updatedQuestion.answers.map(a => 
          a.id === editingAnswer.id ? editingAnswer : a
        );
      }

      const response = await examsService.updateQuestion(examId, question.id, updatedQuestion);
      
      // تحديث الحالة محليًا
      setExam(prevExam => ({
        ...prevExam,
        questions: prevExam.questions.map(q => 
          q.id === updatedQuestion.id ? updatedQuestion : q
        )
      }));
      
      setEditingAnswer(null);
      toastService.success('تم تحديث الإجابة بنجاح');
    } catch (error) {
      console.error('Error saving answer:', error);
      toastService.error('حدث خطأ أثناء حفظ الإجابة');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-5/6 mb-3"></div>
                  <div className="space-y-2">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="h-4 bg-gray-100 dark:bg-gray-600 rounded w-4/5"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">الاختبار غير موجود</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">عذراً، لم يتم العثور على الاختبار المطلوب.</p>
          <div className="mt-6">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowLeftIcon className="ml-2 -mr-1 h-5 w-5" />
              العودة للخلف
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
      {/* زر الإغلاق الجديد */}
      <div className="text-center mb-6">
        <button
          onClick={() => window.close()}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeftIcon className="ml-1 h-4 w-4" />
          إغلاق النافذة
        </button>
      </div>
      
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          {/* رأس الصفحة */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{exam.title}</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  عدد الأسئلة: {exam.questions?.length || 0} سؤال | الدرجة النهائية: {exam.total_score} درجة
                </p>
              </div>
              <div className="mt-4 md:mt-0">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  exam.status === 'published' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                }`}>
                  {exam.status === 'published' ? 'منشور' : 'مسودة'}
                </span>
              </div>
            </div>
          </div>

          {/* محتوى الاختبار */}
          <div className="p-6">
            {exam.description && (
              <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200 mb-2">تعليمات الاختبار</h3>
                <p className="text-blue-700 dark:text-blue-300 whitespace-pre-line">{exam.description}</p>
              </div>
            )}

            <div className="space-y-8">
              {exam.questions?.map((question, qIndex) => (
                <div key={question.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {question.text}
                      </h3>
                      <button
                        onClick={() => setEditingQuestion(question)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                    {(() => {
                      const rawImg = question.image || question.question_image || question.image_url || question.image_path;
                      if (!rawImg) {
                        return null;
                      }
                      const isAbsolute = /^https?:\/\//i.test(rawImg);
                      const src = isAbsolute ? rawImg : `${BASE_URL}${rawImg.startsWith('/') ? '' : '/'}${rawImg}`;
                      return (
                        <div className="mb-4 flex justify-center">
                          <img
                            src={src}
                            alt="صورة السؤال"
                            className="max-w-full h-auto rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 mx-auto"
                            loading="lazy"
                          />
                        </div>
                      );
                    })()}
                    
                    <div className="space-y-2 mt-4">
                      {question.answers
                        ?.slice() // إنشاء نسخة لتجنب تغيير المصفوفة الأصلية
                        .sort((a, b) => a.id - b.id) // ترتيب الإجابات حسب المعرف
                        .map((answer) => (
                        <div 
                          key={answer.id} 
                          className={`p-3 rounded-md border ${answer.is_correct ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'border-gray-200 dark:border-gray-700'}`}
                        >
                          <div className="flex items-start">
                            <span className={`${answer.is_correct ? 'text-green-800 dark:text-green-200 font-medium' : 'text-gray-700 dark:text-gray-300'}`}>
                              {answer.text}
                            </span>
                            <div className="mr-auto flex items-center">
                              <button
                                onClick={() => setEditingAnswer(answer)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 ml-2"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {question.notification && (
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border-l-4 border-blue-500">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <p className="text-sm text-blue-700 dark:text-blue-300">{question.notification}</p>
                        </div>
                      </div>
                    )}
                    
                    {question.explanation && (
                      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md border-l-4 border-blue-500">
                        <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">تفسير الإجابة:</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{question.explanation}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {exam.questions?.length === 0 && (
                <div className="text-center py-12">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">لا توجد أسئلة</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">لم يتم إضافة أي أسئلة لهذا الاختبار بعد.</p>
                </div>
              )}
            </div>
          </div>
          
          {/* تذييل الصفحة */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="ml-2 -mr-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              طباعة الاختبار
            </button>
            <button
              onClick={() => setShowExam(true)}
              className="mr-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-0"
            >
              <PlayIcon className="ml-2 -mr-1 h-5 w-5" />
              بدء الاختبار
            </button>
          </div>
        </div>
      </div>

      {/* Modal for editing question */}
      <Modal
        isOpen={editingQuestion !== null}
        onClose={() => setEditingQuestion(null)}
        title={editingQuestion ? `تعديل السؤال` : ''}
      >
        <div className="mb-4">
          <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            نص السؤال
          </label>
          <textarea
            id="questionText"
            value={editingQuestion?.text || ''}
            onChange={(e) => setEditingQuestion({ ...editingQuestion, text: e.target.value })}
            className="input-base w-full"
            rows={3}
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors duration-200"
            onClick={() => setEditingQuestion(null)}
          >
            إلغاء
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            onClick={handleSaveQuestion}
          >
            حفظ
          </button>
        </div>
        {/* More fields ... */}
      </Modal>

      {/* Modal for editing answer */}
      <Modal
        isOpen={editingAnswer !== null}
        onClose={() => setEditingAnswer(null)}
        title={editingAnswer ? `تعديل الإجابة` : ''}

      >
        <div className="mb-4">
          <label htmlFor="answerText" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            نص الإجابة
          </label>
          <textarea
            id="answerText"
            value={editingAnswer?.text || ''}
            onChange={(e) => setEditingAnswer({ ...editingAnswer, text: e.target.value })}
            className="input-base w-full"
            rows={3}
          />
        </div>
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="isCorrect"
            checked={editingAnswer?.is_correct || false}
            onChange={(e) => setEditingAnswer({ ...editingAnswer, is_correct: e.target.checked })}
            className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="isCorrect" className="mr-2 ml-3 block text-sm text-gray-900 dark:text-gray-300">
            الإجابة الصحيحة
          </label>
          
        </div>
        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-300 dark:bg-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors duration-200"
            onClick={() => setEditingAnswer(null)}
          >
            إلغاء
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            onClick={handleSaveAnswer}
          >
            حفظ
          </button>
        </div>
      </Modal>

      {/* معاينة الاختبار */}
      {showExam && exam && (
        <div className="mt-6 w-full">
          <Content
            currentLesson={{
              ...exam,
              type: 'quiz',
              title: exam.title,
              duration: exam.duration || 20,
              questions_count: exam.questions?.length || 0
            }}
            isQuizStarted={isQuizStarted}
            showResults={showResults}
            isLoading={loading}
            showDetailedResults={showDetailedResults}
            handleQuizReset={handleQuizReset}
            setIsQuizStarted={setIsQuizStarted}
            handleShowResults={() => setShowResults(true)}
            setShowDetailedResults={setShowDetailedResults}
            currentQuestionIndex={currentQuestionIndex}
            timeLeft={timeLeft}
            formatTime={(seconds) => {
              const mins = Math.floor(seconds / 60);
              const secs = seconds % 60;
              return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
            }}
            quizData={{
              questions: exam.questions?.map(question => {
                const rawImg = question.image || question.question_image || question.image_url || question.image_path || null;
                const isAbsolute = rawImg ? /^https?:\/\//i.test(rawImg) : false;
                const normalizedImage = rawImg ? (isAbsolute ? rawImg : `${BASE_URL}${rawImg.startsWith('/') ? '' : '/'}${rawImg}`) : null;
                // Normalize answers images
                const answers = (question.answers || [])
                  .slice()
                  .sort((a, b) => a.id - b.id)
                  .map(ans => {
                    const aRaw = ans.image || ans.image_path || null;
                    const aAbs = aRaw ? /^https?:\/\//i.test(aRaw) : false;
                    const aImg = aRaw ? (aAbs ? aRaw : `${BASE_URL}${aRaw.startsWith('/') ? '' : '/'}${aRaw}`) : null;
                    return { ...ans, image: aImg };
                  });
                return ({
                  ...question,
                  image: normalizedImage,
                  answers,
                  correctAnswer: question.answers?.find(a => a.is_correct)?.id
                });
              }) || [],
              duration: exam.duration || 20,
              passing_score: exam.passing_score || 50
            }}
            currentAnswer={currentAnswer}
            handleAnswerSelect={handleAnswerSelect}
            quizAnswers={quizAnswers}
            handlePreviousQuestion={handlePreviousQuestion}
            handleNextQuestion={handleNextQuestion}
            calculateScore={() => {
              const totalQuestions = exam.questions?.length || 0;
              if (totalQuestions === 0) return { correct: 0, total: 0, percentage: 0 };
              
              let correctAnswers = 0;
              exam.questions?.forEach((question, index) => {
                const userAnswerId = quizAnswers[index];
                if (userAnswerId) {
                  // البحث عن الإجابة المختارة في مصفوفة إجابات السؤال
                  const selectedAnswer = question.answers.find(a => a.id === userAnswerId);
                  if (selectedAnswer?.is_correct) {
                    correctAnswers++;
                  }
                }
              });
              
              return {
                correct: correctAnswers,
                total: totalQuestions,
                percentage: totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0
              };
            }}
            isSidebarOpen={false}
          />
        </div>
      )}
    </div>
  );
};

export default ExamPreviewPage;
